import React from "react";
import { MarketingShell, Container, Eyebrow, useSeo, FONT } from "./Marketing";

const SECTIONS = [
  {
    n: 1, title: "About these Terms",
    body: [
      "These Terms of Service (\u201cTerms\u201d) govern your access to and use of the AI Turf Designer application, website and related services (\u201cService\u201d). The Service is provided by Prime Gym Turf LTD (\u201cAI Turf Designer\u201d, \u201cwe\u201d, \u201cus\u201d or \u201cour\u201d). By accessing or using the Service, you agree to these Terms. If you are using the Service on behalf of a business, you confirm that you have authority to bind that business to these Terms.",
    ],
  },
  {
    n: 2, title: "Our details",
    body: ["Prime Gym Turf LTD", "31 Belmont Road", "Wolverhampton", "WV4 5UD", "United Kingdom", "Email: primegymturf@gmail.com", "Website: https://aiturfdesigner.com/"],
  },
  {
    n: 3, title: "The Service",
    body: [
      "AI Turf Designer provides an online tool for creating, customising and configuring bespoke gym turf and related products. Features may include manual design tools, AI-assisted design generation, pricing, order configuration and Shopify-integrated checkout. We may update, improve, modify or discontinue parts of the Service from time to time, including individual features, templates and integrations.",
    ],
  },
  {
    n: 4, title: "Accounts and access",
    body: [
      "You are responsible for providing accurate information where required to use the Service and for keeping any account credentials secure. You must not share access credentials with unauthorised persons, attempt to gain unauthorised access to the Service, interfere with its operation, or use it in a way that could damage, overload or compromise the Service.",
    ],
  },
  {
    n: 5, title: "Use of the design tools",
    body: [
      "You may use AI Turf Designer to create turf designs using the available manual tools, templates and AI-assisted functionality. Design previews are intended to assist with visualisation. Colours, proportions, image quality and appearance on screen may differ from the final manufactured product. Where a design is intended for manufacture, the final production specification should be reviewed and confirmed before production begins.",
    ],
  },
  {
    n: 6, title: "AI-generated designs",
    body: [
      "AI Turf Designer may use OpenAI technology to generate images and design concepts. AI-generated output can vary and may not always be accurate, suitable or technically suitable for manufacture. AI-generated designs may contain unintended visual elements, inaccuracies or imperfections. We do not guarantee that AI-generated artwork will be unique, error-free or suitable for production without review. You are responsible for reviewing and approving a design before placing an order.",
    ],
  },
  {
    n: 7, title: "Uploaded content and intellectual property",
    body: [
      "You retain ownership of logos, artwork and other content that you upload, subject to any rights belonging to third parties. You confirm that you have the necessary rights, licences and permissions to upload and use any logo, image, artwork, text or other material submitted through the Service. You must not upload material that infringes another person's intellectual property, privacy or other rights, or that is unlawful. You grant us a limited, non-exclusive right to process and reproduce uploaded content solely as necessary to provide the Service, generate designs, prepare previews and fulfil orders.",
    ],
  },
  {
    n: 8, title: "AI-generated and platform content",
    body: [
      "The Service, including its software, interface, templates, branding, underlying technology and platform content, is owned by or licensed to Prime Gym Turf LTD and is protected by applicable intellectual property laws. Subject to these Terms, we grant you a limited right to use the Service for its intended purpose. You may not copy, reproduce, reverse engineer, resell, scrape or commercially exploit the Service or its underlying technology except where expressly permitted by us or applicable law.",
    ],
  },
  {
    n: 9, title: "Pricing and quotations",
    body: [
      "Where the Service displays a price, that price is calculated from the configuration supplied by the user and the applicable tenant pricing rules. Prices may change before an order is placed. A displayed configuration or quotation does not constitute acceptance of an order until the applicable ordering and payment process has been completed. Where applicable, taxes, delivery charges, installation charges or other additional costs will be identified during the ordering process or quotation process.",
    ],
  },
  {
    n: 10, title: "Shopify orders and payment",
    body: [
      "For Shopify-connected merchants, AI Turf Designer may create a Shopify Draft Order containing the configured design and calculated price. Customers may then be redirected to Shopify's checkout to complete payment. An order is not accepted solely because a design has been created or a price has been displayed. Acceptance and payment are subject to the applicable merchant's ordering terms and Shopify's checkout and payment processes. The merchant supplying the product remains responsible for fulfilment, production, delivery and any merchant-specific terms that apply to the sale.",
    ],
  },
  {
    n: 11, title: "Turnaround and delivery",
    body: [
      "Production and turnaround times shown through AI Turf Designer are estimates or merchant-configured timeframes unless expressly stated otherwise. Different merchants may offer different standard or express turnaround options. Availability of an express option may depend on the selected configuration, including dimensions, and the rules configured by the relevant merchant. Turnaround estimates generally relate to production and do not necessarily include delivery time unless expressly stated. Delivery, installation and site-specific requirements may be subject to separate terms or charges.",
    ],
  },
  {
    n: 12, title: "Orders, cancellations and refunds",
    body: [
      "Because bespoke turf designs and products may be manufactured specifically to a customer's configuration, cancellation and refund rights may be limited once production has commenced, subject to applicable consumer protection law. Nothing in these Terms excludes or limits any statutory rights that cannot lawfully be excluded or limited. For Shopify purchases, customers should also review the applicable merchant's terms and Shopify checkout information.",
    ],
  },
  {
    n: 13, title: "Design approval",
    body: [
      "Before production, customers are responsible for checking the dimensions, wording, logos, colours, markings, lane configuration and other design specifications supplied for an order. Once a bespoke product has entered production, changes may not be possible or may incur additional costs.",
    ],
  },
  {
    n: 14, title: "Prohibited use",
    body: [
      "Use the Service for unlawful purposes. Upload content you do not have permission to use. Attempt to bypass security, authentication, pricing or size restrictions. Interfere with the operation or availability of the Service. Use automated methods to scrape, copy or systematically extract Service content without permission. Introduce malware, malicious code or other harmful material. Attempt to reverse engineer or obtain source code except where applicable law expressly permits it.",
    ],
  },
  {
    n: 15, title: "Availability and third-party services",
    body: [
      "We aim to keep the Service available and reliable but do not guarantee uninterrupted or error-free availability. The Service may depend on third-party services, including Shopify, OpenAI, hosting, database, email and other infrastructure providers. We are not responsible for outages or failures of third-party services outside our reasonable control. Third-party services may have their own terms and privacy policies.",
    ],
  },
  {
    n: 16, title: "Disclaimer",
    body: [
      "To the maximum extent permitted by law, the Service and design-generation functionality are provided on an 'as available' basis. We do not guarantee that the Service, AI-generated content or design previews will always be accurate, uninterrupted or suitable for every purpose. A design preview is not a substitute for reviewing the final production specification.",
    ],
  },
  {
    n: 17, title: "Limitation of liability",
    body: [
      "Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or limited, including liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or other liability that applicable law does not permit us to exclude. Subject to the above, we will not be liable for indirect or consequential losses, loss of profits, loss of business, loss of opportunity or loss of data arising from use of the Service, except where such liability cannot lawfully be excluded. To the extent permitted by law, our total liability arising from your use of the Service will be limited to the amount paid by you to us for the relevant Service in the twelve months preceding the event giving rise to the claim.",
    ],
  },
  {
    n: 18, title: "Indemnity",
    body: [
      "Where permitted by law, you agree to compensate us for reasonable losses, claims, liabilities and costs arising from your breach of these Terms or your unlawful use of the Service, including claims arising from content you upload where you did not have the necessary rights or permissions.",
    ],
  },
  {
    n: 19, title: "Suspension and termination",
    body: [
      "We may suspend or terminate access where reasonably necessary to protect the Service, prevent abuse, comply with law, address security concerns or respond to a material breach of these Terms. Where practicable, we will provide reasonable notice before termination unless immediate action is required for security, legal or operational reasons.",
    ],
  },
  {
    n: 20, title: "Privacy",
    body: [
      "Our processing of personal information is described in the AI Turf Designer Privacy Policy, available at https://aiturfdesigner.com/privacy-policy. The Privacy Policy forms part of the information governing use of the Service but does not replace these Terms.",
    ],
  },
  {
    n: 21, title: "Changes to these Terms",
    body: [
      "We may update these Terms from time to time. The latest version will be published at https://aiturfdesigner.com/terms. If we make material changes, we will take reasonable steps to bring them to users' attention. Continued use of the Service after updated Terms take effect constitutes acceptance of the updated Terms, where permitted by law.",
    ],
  },
  {
    n: 22, title: "Governing law and jurisdiction",
    body: [
      "These Terms are governed by the laws of England and Wales, unless applicable law requires otherwise. Subject to any mandatory consumer rights, the courts of England and Wales will have jurisdiction over disputes arising from these Terms.",
    ],
  },
  {
    n: 23, title: "Contact",
    body: ["Questions about these Terms can be sent to:", "Prime Gym Turf LTD", "31 Belmont Road", "Wolverhampton", "WV4 5UD", "United Kingdom", "Email: primegymturf@gmail.com"],
  },
];

export default function Terms() {
  useSeo(
    "Terms of Service | AI Turf Designer",
    "The Terms of Service governing use of the AI Turf Designer application, website and Shopify integration, provided by Prime Gym Turf LTD."
  );
  return (
    <MarketingShell>
      <section className="border-b border-white/10 bg-[#0a0a0a]">
        <Container className="py-16 md:py-20">
          <Eyebrow>Legal</Eyebrow>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white" style={{ fontFamily: FONT.display }} data-testid="terms-title">
            Terms of Service
          </h1>
          <p className="mt-4 text-zinc-500 text-sm" data-testid="terms-updated">Last updated: 2 September 2026</p>
        </Container>
      </section>

      <section>
        <Container className="py-12 md:py-16 max-w-4xl">
          <div className="space-y-10" data-testid="terms-content">
            {SECTIONS.map((s) => (
              <div key={s.n} data-testid={`terms-section-${s.n}`}>
                <h2 className="text-lg md:text-xl font-bold uppercase tracking-wide text-white" style={{ fontFamily: FONT.display }}>
                  {s.n}. {s.title}
                </h2>
                <div className="mt-3 space-y-3">
                  {(s.body || []).map((p, i) => (
                    <p key={i} className="text-zinc-400 text-base leading-relaxed">{p}</p>
                  ))}
                  {s.bullets && (
                    <ul className="list-disc pl-6 space-y-2 marker:text-[#00D26A]">
                      {s.bullets.map((b, i) => <li key={i} className="text-zinc-400 text-base leading-relaxed">{b}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>
    </MarketingShell>
  );
}
